import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Video, Radio, Users, Send, MessageCircle,
  Mic, MicOff, Camera, CameraOff, StopCircle, Eye, Play,
  RotateCcw, Share2, Trash2, Save, X, Facebook, Twitter, Linkedin, Copy,
  Settings, SkipForward, Monitor, MonitorOff, LayoutGrid, Square, Columns,
  Volume2, Headphones
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDistanceToNow } from "date-fns";

// ICE servers including free TURN relay (handles NAT traversal)
const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "turn:standard.relay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:standard.relay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
  { urls: "turn:standard.relay.metered.ca:443?transport=tcp", username: "openrelayproject", credential: "openrelayproject" },
];

function CameraPreview({ onStreamReady }) {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        if (videoRef.current) videoRef.current.srcObject = stream;
        setReady(true);
        onStreamReady(stream);
      } catch (err) { setError(err.message); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden bg-black aspect-video max-w-lg">
      <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
      {!ready && !error && <div className="absolute inset-0 flex items-center justify-center"><span className="text-white text-sm animate-pulse">Starting camera...</span></div>}
      {error && <div className="absolute inset-0 flex items-center justify-center bg-slate-900"><CameraOff className="w-10 h-10 text-red-400 mx-auto mb-2" /><p className="text-red-400 text-sm text-center p-4">{error}</p></div>}
      {ready && <div className="absolute top-3 left-3 bg-green-500/80 text-white text-xs px-2 py-1 rounded flex items-center gap-1"><Camera className="w-3 h-3" /> Ready</div>}
    </div>
  );
}

function BroadcasterView({ stream, user, localStream, onEnd, onStreamUpdate }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const peersRef = useRef({});
  const recorderRef = useRef(null);
  const fullRecordRef = useRef([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [facingMode, setFacingMode] = useState("user");
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && localStream) videoRef.current.srcObject = localStream;

    // Record full stream for saving
    try {
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus' : 'video/webm';
      const mr = new MediaRecorder(localStream, { mimeType });
      mr.ondataavailable = (e) => { if (e.data.size > 0) fullRecordRef.current.push(e.data); };
      mr.start(2000); // Collect data every 2 seconds
      recorderRef.current = mr;
    } catch (e) { console.warn("Recording not available:", e); }

    // WebSocket for signaling + chat
    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/stream/${stream.stream_id}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "broadcaster_ready" }));
      console.log("[STREAM]", "WebSocket connected, broadcaster ready");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "chat") setChatMessages(prev => [...prev, data]);
      else if (data.type === "viewer_count") setViewerCount(data.count);
      else if (data.type === "viewer_joined") {
        const viewerId = data.viewer_id;
        console.log("[STREAM]", "Viewer joined: " + viewerId);
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        peersRef.current[viewerId] = pc;

        localStream.getTracks().forEach(t => pc.addTrack(t, localStream));
        console.log("[STREAM]", "Added " + localStream.getTracks().length + " tracks");

        pc.onicecandidate = (e) => {
          if (e.candidate) ws.send(JSON.stringify({ type: "ice_candidate", candidate: e.candidate, target: viewerId }));
        };

        pc.onconnectionstatechange = () => console.log("[STREAM]", "Peer " + viewerId + ": " + pc.connectionState);

        pc.createOffer().then(offer => {
          pc.setLocalDescription(offer);
          ws.send(JSON.stringify({ type: "offer", sdp: offer, target: viewerId }));
          console.log("[STREAM]", "Sent offer to " + viewerId);
        }).catch(err => console.log("[STREAM]", "Offer error: " + err.message));
      }
      else if (data.type === "answer" && data.from) {
        console.log("[STREAM]", "Got answer from " + data.from);
        const pc = peersRef.current[data.from];
        if (pc) pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
      }
      else if (data.type === "ice_candidate" && data.from) {
        const pc = peersRef.current[data.from];
        if (pc && data.candidate) pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
      }
      else if (data.type === "viewer_left" && data.viewer_id) {
        console.log("[STREAM]", "Viewer left: " + data.viewer_id);
        const pc = peersRef.current[data.viewer_id];
        if (pc) { pc.close(); delete peersRef.current[data.viewer_id]; }
      }
    };

    return () => {
      Object.values(peersRef.current).forEach(pc => pc.close());
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const handleEnd = async (action = "save") => {
    // Wait for recorder to finish and collect final chunk
    const waitForRecorder = () => new Promise((resolve) => {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.onstop = () => resolve();
        recorderRef.current.stop();
      } else {
        resolve();
      }
    });
    
    await waitForRecorder();
    Object.values(peersRef.current).forEach(pc => pc.close());
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    
    try {
      await axios.post(`${API}/streams/${stream.stream_id}/end`, {}, { withCredentials: true });
      
      if (action === "save" && fullRecordRef.current.length > 0) {
        toast.info("Saving recording...");
        const blob = new Blob(fullRecordRef.current, { type: "video/webm" });
        const fd = new FormData();
        fd.append("file", blob, "recording.webm");
        await axios.post(`${API}/streams/${stream.stream_id}/recording`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Stream saved!");
      } else if (action === "delete") {
        await axios.delete(`${API}/streams/${stream.stream_id}`, { withCredentials: true }).catch(() => {});
        toast.success("Stream deleted");
      } else if (action === "share") {
        const url = `${window.location.origin}/live/${stream.stream_id}`;
        navigator.clipboard?.writeText(url);
        if (fullRecordRef.current.length > 0) {
          toast.info("Saving recording...");
          const blob = new Blob(fullRecordRef.current, { type: "video/webm" });
          const fd = new FormData();
          fd.append("file", blob, "recording.webm");
          await axios.post(`${API}/streams/${stream.stream_id}/recording`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
        }
        toast.success("Stream link copied & recording saved!");
      } else {
        toast.success("Stream ended");
      }
    } catch { toast.error("Failed to end"); }
    setEndDialogOpen(false);
    onEnd();
  };

  const rotateCamera = async () => {
    const newMode = facingMode === "user" ? "environment" : "user";
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      // Stop old tracks
      localStream.getTracks().forEach(t => t.stop());
      // Update video element
      if (videoRef.current) videoRef.current.srcObject = newStream;
      // Update peer connections with new tracks
      Object.values(peersRef.current).forEach(pc => {
        const senders = pc.getSenders();
        newStream.getTracks().forEach(track => {
          const sender = senders.find(s => s.track?.kind === track.kind);
          if (sender) sender.replaceTrack(track);
        });
      });
      setFacingMode(newMode);
      // Update parent's stream reference
      onStreamUpdate?.(newStream);
    } catch { toast.error("Cannot switch camera"); }
  };

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "chat", user_name: user?.name || "Anonymous", content: chatInput }));
    setChatInput("");
  };

  return (
    <section className="bg-slate-900 min-h-[calc(100vh-64px)]">
      <div className="container-custom py-4">
        <div className="grid lg:grid-cols-[1fr_350px] gap-4">
          <div>
            <div className="aspect-video bg-black rounded-xl overflow-hidden relative">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} />
              <div className="absolute top-4 left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1.5"><Radio className="w-3 h-3 animate-pulse" /> LIVE</div>
              <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1.5"><Eye className="w-4 h-4" /> {viewerCount}</div>
              <img src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png" alt="Blue vs Blue" className="absolute bottom-3 right-3 h-12 w-auto opacity-80 rounded z-10" />
            </div>
            <div className="flex items-center justify-between mt-4">
              <div><h2 className="text-white text-lg font-bold">{stream.title}</h2><p className="text-slate-400 text-sm">You are broadcasting</p></div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={rotateCamera} className="border-slate-600 text-white" data-testid="rotate-camera"><RotateCcw className="w-4 h-4" /></Button>
                <Button size="sm" variant={audioOn ? "outline" : "destructive"} onClick={() => { localStream?.getAudioTracks().forEach(t => { t.enabled = !t.enabled; }); setAudioOn(p => !p); }} className="border-slate-600 text-white">{audioOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}</Button>
                <Button size="sm" variant={videoOn ? "outline" : "destructive"} onClick={() => { localStream?.getVideoTracks().forEach(t => { t.enabled = !t.enabled; }); setVideoOn(p => !p); }} className="border-slate-600 text-white">{videoOn ? <Camera className="w-4 h-4" /> : <CameraOff className="w-4 h-4" />}</Button>
                <Button size="sm" onClick={() => setEndDialogOpen(true)} className="bg-red-500 text-white hover:bg-red-600" data-testid="end-stream-btn"><StopCircle className="w-4 h-4 mr-1" /> End</Button>
              </div>
            </div>
          </div>

          {/* End Stream Dialog */}
          <Dialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
            <DialogContent className="sm:max-w-sm" data-testid="end-stream-dialog">
              <DialogHeader><DialogTitle>End Stream</DialogTitle></DialogHeader>
              <div className="space-y-2">
                <Button onClick={() => handleEnd("save")} className="w-full bg-deep-blue text-white justify-start gap-2"><Save className="w-4 h-4" /> Save & Close</Button>
                <Button onClick={() => handleEnd("share")} variant="outline" className="w-full justify-start gap-2"><Share2 className="w-4 h-4" /> Share Link & Close</Button>
                <Button onClick={() => handleEnd("delete")} variant="outline" className="w-full justify-start gap-2 text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /> Delete Stream</Button>
                <Button onClick={() => handleEnd("close")} variant="ghost" className="w-full justify-start gap-2 text-slate-500"><X className="w-4 h-4" /> Just Close</Button>
              </div>
            </DialogContent>
          </Dialog>
          <div className="bg-slate-800 rounded-xl flex flex-col h-[calc(100vh-120px)] max-h-[600px]">
            <div className="p-3 border-b border-slate-700"><h3 className="text-white font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Live Chat</h3></div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">{chatMessages.map((m, i) => <div key={i} className="text-sm"><span className="font-semibold text-awareness-blue">{m.user_name}: </span><span className="text-slate-300">{m.content}</span></div>)}<div ref={chatEndRef} /></div>
            {user && <div className="p-3 border-t border-slate-700 flex gap-2"><Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message..." className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400" /><Button size="sm" onClick={sendChat} className="bg-awareness-blue text-white"><Send className="w-4 h-4" /></Button></div>}
          </div>
        </div>
      </div>
    </section>
  );
}

// Standalone video-only stream viewer — same WebRTC as ViewerView, renders only <video>
export function StreamVideo({ streamId }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const myIdRef = useRef(null);
  const [status, setStatus] = useState("Connecting...");

  useEffect(() => {
    if (!streamId) return;
    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/stream/${streamId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "viewer_ready" }));
      setStatus("Waiting...");
      const retryInterval = setInterval(() => {
        if (pcRef.current) { clearInterval(retryInterval); return; }
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "viewer_ready" }));
        else clearInterval(retryInterval);
      }, 5000);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "your_id") myIdRef.current = data.id;
      else if (data.type === "offer") {
        setStatus("Connecting...");
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;
        pc.ontrack = (ev) => {
          setStatus("Live");
          if (videoRef.current) { videoRef.current.srcObject = ev.streams[0]; videoRef.current.play().catch(() => {}); }
        };
        pc.onicecandidate = (e) => { if (e.candidate) ws.send(JSON.stringify({ type: "ice_candidate", candidate: e.candidate, target: "broadcaster" })); };
        pc.onconnectionstatechange = () => { if (pc.connectionState === "connected") setStatus("Live"); };
        pc.setRemoteDescription(new RTCSessionDescription(data.sdp))
          .then(() => pc.createAnswer())
          .then(answer => { pc.setLocalDescription(answer); ws.send(JSON.stringify({ type: "answer", sdp: answer, from: myIdRef.current })); })
          .catch(() => setStatus("Error"));
      }
      else if (data.type === "ice_candidate" && data.from === "broadcaster") {
        if (pcRef.current && data.candidate) pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
      }
    };

    return () => { if (pcRef.current) pcRef.current.close(); if (ws.readyState === WebSocket.OPEN) ws.close(); };
  }, [streamId]);

  return (
    <div className="relative w-full h-full bg-black">
      <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
      {status !== "Live" && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <p className="text-white text-sm animate-pulse">{status}</p>
        </div>
      )}
    </div>
  );
}

export function ViewerView({ stream, user }) {
  const videoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const myIdRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [isLive, setIsLive] = useState(true);
  const [status, setStatus] = useState("Connecting...");
  const chatEndRef = useRef(null);
  
  // Director mode
  const isDirector = new URLSearchParams(window.location.search).get("director") === "true";
  const isAdmin = user?.role === "super_admin" || user?.role === "master_admin" || user?.role === "developer" || user?.role === "admin";

  useEffect(() => {
    const wsUrl = API.replace("/api", "").replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/stream/${stream.stream_id}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "viewer_ready" }));
      setStatus("Waiting for stream...");
      console.log("[STREAM]", "WebSocket connected, sent viewer_ready");
      
      // Retry viewer_ready every 5 seconds until we get an offer
      const retryInterval = setInterval(() => {
        if (pcRef.current) { clearInterval(retryInterval); return; } // Got offer, stop retrying
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "viewer_ready" }));
          console.log("[STREAM]", "Retrying viewer_ready...");
        } else {
          clearInterval(retryInterval);
        }
      }, 5000);
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log("[STREAM]", "Received: " + data.type);
      if (data.type === "chat") setChatMessages(prev => [...prev, data]);
      else if (data.type === "viewer_count") setViewerCount(data.count);
      else if (data.type === "your_id") { myIdRef.current = data.id; }
      else if (data.type === "stream_ended") { setIsLive(false); setStatus("Stream ended"); }
      else if (data.type === "offer") {
        console.log("[STREAM]", "Received offer from broadcaster");
        setStatus("Connecting to broadcaster...");
        const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
        pcRef.current = pc;

        pc.ontrack = (event) => {
          console.log("[STREAM]", "Got video track! Setting video source");
          setStatus("Live");
          if (videoRef.current) {
            videoRef.current.srcObject = event.streams[0];
            videoRef.current.play().catch(() => {});
          }
        };

        pc.onicecandidate = (e) => {
          if (e.candidate) {
            console.log("[STREAM]", "Sending ICE candidate");
            ws.send(JSON.stringify({ type: "ice_candidate", candidate: e.candidate, target: "broadcaster" }));
          }
        };

        pc.onconnectionstatechange = () => {
          console.log("[STREAM]", "Connection: " + pc.connectionState);
          if (pc.connectionState === "connected") setStatus("Live");
          else if (pc.connectionState === "failed") setStatus("Connection failed");
        };

        pc.oniceconnectionstatechange = () => {
          console.log("[STREAM]", "ICE: " + pc.iceConnectionState);
        };

        pc.setRemoteDescription(new RTCSessionDescription(data.sdp))
          .then(() => { console.log("[STREAM]", "Set remote description, creating answer"); return pc.createAnswer(); })
          .then(answer => {
            pc.setLocalDescription(answer);
            ws.send(JSON.stringify({ type: "answer", sdp: answer, from: myIdRef.current }));
            console.log("[STREAM]", "Sent answer to broadcaster");
          })
          .catch(err => { setStatus("Connection error"); console.log("[STREAM]", "Error: " + err.message); });
      }
      else if (data.type === "ice_candidate" && data.from === "broadcaster") {
        if (pcRef.current && data.candidate) {
          pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
          console.log("[STREAM]", "Added broadcaster ICE candidate");
        }
      }
    };

    ws.onerror = () => console.log("[STREAM]", "WebSocket error");
    ws.onclose = () => {
      console.log("[STREAM]", "WebSocket closed");
      // Auto-reconnect after 3 seconds if stream is still live
      if (isLive) {
        setTimeout(() => {
          if (isLive) {
            console.log("[STREAM]", "Auto-reconnecting...");
            setStatus("Reconnecting...");
            pcRef.current = null;
            const newWs = new WebSocket(`${wsUrl}/api/ws/stream/${stream.stream_id}`);
            wsRef.current = newWs;
            newWs.onopen = ws.onopen;
            newWs.onmessage = ws.onmessage;
            newWs.onerror = ws.onerror;
            newWs.onclose = ws.onclose;
          }
        }, 3000);
      }
    };

    return () => {
      if (pcRef.current) pcRef.current.close();
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, []);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatMessages]);

  const sendChat = () => {
    if (!chatInput.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({ type: "chat", user_name: user?.name || "Anonymous", content: chatInput }));
    setChatInput("");
  };

  // Director state
  const [directorOpen, setDirectorOpen] = useState(isDirector && isAdmin);
  const [overlayText, setOverlayText] = useState("");
  const [showOverlay, setShowOverlay] = useState(false);
  const [scrollText, setScrollText] = useState("");
  const [scrollActive, setScrollActive] = useState(false);
  const [showCommercial, setShowCommercial] = useState(false);
  const [showWeather, setShowWeather] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherCity, setWeatherCity] = useState("Indianapolis");
  const [musicUrl, setMusicUrl] = useState("");
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [musicVolume, setMusicVolume] = useState(50);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const musicAudioRef = useRef(null);

  const fetchWeather = async () => {
    try {
      const r = await fetch(`https://wttr.in/${weatherCity}?format=j1`);
      const d = await r.json();
      setWeatherData({ temp: d.current_condition?.[0]?.temp_F + "°F", desc: d.current_condition?.[0]?.weatherDesc?.[0]?.value, city: weatherCity });
    } catch { setWeatherData({ temp: "72°F", desc: "Partly Cloudy", city: weatherCity }); }
  };

  const playMusic = (url) => {
    if (musicAudioRef.current) { musicAudioRef.current.pause(); musicAudioRef.current = null; }
    if (!url) return;
    const audio = new Audio(url);
    audio.volume = musicVolume / 100;
    audio.loop = true;
    audio.play().catch(() => toast.error("Click page first to enable audio"));
    musicAudioRef.current = audio;
    setMusicPlaying(true);
  };

  const stopMusic = () => {
    if (musicAudioRef.current) { musicAudioRef.current.pause(); musicAudioRef.current = null; }
    setMusicPlaying(false);
  };

  const playSFX = (type) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator(); const g = ctx.createGain();
    osc.connect(g); g.connect(ctx.destination); g.gain.value = 0.3;
    const fx = { Intro: [523,"sine"], Applause: [300,"square"], "Drum Roll": [200,"triangle"], "Air Horn": [440,"sawtooth"], Transition: [660,"triangle"], Ding: [880,"sine"], Buzzer: [150,"sawtooth"], Chime: [1047,"sine"] };
    const [freq, type2] = fx[type] || [880, "sine"];
    osc.frequency.value = freq; osc.type = type2;
    g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    osc.start(); osc.stop(ctx.currentTime + 0.8);
  };

  return (
    <section className="bg-slate-900 min-h-[calc(100vh-64px)]">
      <div className="container-custom py-4">
        <div className={`grid gap-4 ${directorOpen ? 'lg:grid-cols-[250px_1fr_300px]' : 'lg:grid-cols-[1fr_350px]'}`}>
          
          {/* Director Panel */}
          {directorOpen && (
            <div className="bg-slate-800 rounded-xl p-3 space-y-3 overflow-y-auto max-h-[calc(100vh-120px)]">
              <h3 className="text-white font-bold text-sm flex items-center gap-2"><Settings className="w-4 h-4 text-awareness-blue" /> Director</h3>
              
              {/* Commercial */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Commercial</span>
                <Button size="sm" variant={showCommercial ? "destructive" : "outline"} onClick={() => setShowCommercial(!showCommercial)} className="w-full mt-1 border-slate-600 text-white text-xs">
                  {showCommercial ? <><Play className="w-3 h-3 mr-1" /> Back to Live</> : <><SkipForward className="w-3 h-3 mr-1" /> Commercial Break</>}
                </Button>
              </div>

              {/* Text Overlay */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Text Overlay</span>
                <Input value={overlayText} onChange={(e) => setOverlayText(e.target.value)} placeholder="Lower third text..." className="bg-slate-700 border-slate-600 text-white text-xs mt-1" />
                <div className="flex gap-1 mt-1">
                  <Button size="sm" onClick={() => setShowOverlay(true)} className="flex-1 bg-awareness-blue text-white text-[10px]">Show</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowOverlay(false)} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Hide</Button>
                </div>
              </div>

              {/* News Ticker */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">News Ticker</span>
                <Input value={scrollText} onChange={(e) => setScrollText(e.target.value)} placeholder="Scrolling text..." className="bg-slate-700 border-slate-600 text-white text-xs mt-1" />
                <div className="flex gap-1 mt-1">
                  <Button size="sm" onClick={() => setScrollActive(true)} className="flex-1 bg-awareness-blue text-white text-[10px]">Start</Button>
                  <Button size="sm" variant="outline" onClick={() => setScrollActive(false)} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Stop</Button>
                </div>
              </div>

              {/* Weather */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Weather</span>
                <div className="flex gap-1 mt-1">
                  <Input value={weatherCity} onChange={(e) => setWeatherCity(e.target.value)} className="bg-slate-700 border-slate-600 text-white text-xs" placeholder="City" />
                  <Button size="sm" variant="outline" onClick={fetchWeather} className="border-slate-600 text-slate-300 text-[10px] shrink-0">Get</Button>
                </div>
                {weatherData && <p className="text-slate-300 text-[10px] mt-1">{weatherData.temp} — {weatherData.desc}</p>}
                <div className="flex gap-1 mt-1">
                  <Button size="sm" onClick={() => { if (!weatherData) fetchWeather(); setShowWeather(true); }} className="flex-1 bg-awareness-blue text-white text-[10px]">Show</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowWeather(false)} className="flex-1 border-slate-600 text-slate-300 text-[10px]">Hide</Button>
                </div>
              </div>

              {/* Audio Mixer */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Audio Mixer</span>
                <div className="mt-1 space-y-2">
                  <div>
                    <div className="flex justify-between"><span className="text-white text-[10px]"><Mic className="w-3 h-3 inline mr-1" />Mic</span><span className="text-slate-400 text-[10px]">{volume}%</span></div>
                    <input type="range" min="0" max="100" value={volume} onChange={(e) => setVolume(Number(e.target.value))} className="w-full accent-green-500 h-1" />
                  </div>
                  <div>
                    <div className="flex justify-between"><span className="text-white text-[10px]"><Headphones className="w-3 h-3 inline mr-1" />Music</span><span className="text-slate-400 text-[10px]">{musicVolume}%</span></div>
                    <input type="range" min="0" max="100" value={musicVolume} onChange={(e) => { setMusicVolume(Number(e.target.value)); if (musicAudioRef.current) musicAudioRef.current.volume = e.target.value / 100; }} className="w-full accent-blue-500 h-1" />
                  </div>
                </div>
              </div>

              {/* Music Player */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Music</span>
                <input type="file" accept="audio/*" onChange={(e) => { const f = e.target.files[0]; if (f) { const url = URL.createObjectURL(f); setMusicUrl(url); playMusic(url); } }} className="block w-full text-[10px] text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-[10px] file:bg-slate-700 file:text-slate-300 mt-1" />
                <Input value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} placeholder="Or paste URL..." className="bg-slate-700 border-slate-600 text-white text-xs mt-1" />
                <div className="flex gap-1 mt-1">
                  <Button size="sm" onClick={() => playMusic(musicUrl)} disabled={!musicUrl} className="flex-1 bg-green-600 text-white text-[10px]"><Play className="w-3 h-3 mr-1" /> Play</Button>
                  <Button size="sm" onClick={stopMusic} variant="outline" className="flex-1 border-slate-600 text-slate-300 text-[10px]"><StopCircle className="w-3 h-3 mr-1" /> Stop</Button>
                </div>
                {musicPlaying && <p className="text-green-400 text-[10px] mt-1 animate-pulse">Playing...</p>}
              </div>

              {/* Sound Effects */}
              <div>
                <span className="text-awareness-blue text-[10px] font-bold uppercase">Sound Effects</span>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {[
                    {id:"Intro",c:"bg-blue-600"},{id:"Applause",c:"bg-green-600"},{id:"Drum Roll",c:"bg-amber-600"},{id:"Air Horn",c:"bg-red-600"},
                    {id:"Transition",c:"bg-purple-600"},{id:"Ding",c:"bg-cyan-600"},{id:"Buzzer",c:"bg-orange-600"},{id:"Chime",c:"bg-pink-600"}
                  ].map(fx => (
                    <button key={fx.id} onClick={() => playSFX(fx.id)} className={`${fx.c} text-white text-[10px] font-bold py-1.5 rounded hover:opacity-80 active:scale-95 transition-transform`}>{fx.id}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Video */}
          <div>
            <div className="aspect-video bg-black rounded-xl overflow-hidden relative">
              <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
              {/* Weather overlay — top left */}
              {showWeather && weatherData && <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-lg z-20"><p className="text-lg font-bold">{weatherData.temp}</p><p className="text-[10px] text-white/70">{weatherData.desc}</p><p className="text-[10px] text-white/50">{weatherData.city}</p></div>}
              
              {/* LIVE badge — below weather when shown, top-left when no weather */}
              {isLive && <div className={`absolute left-4 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full flex items-center gap-1.5 z-20 ${showWeather && weatherData ? 'top-20' : 'top-4'}`}><Radio className="w-3 h-3 animate-pulse" /> LIVE</div>}
              
              <div className="absolute top-4 right-4 bg-black/60 text-white text-sm px-3 py-1 rounded-full flex items-center gap-1.5"><Eye className="w-4 h-4" /> {viewerCount}</div>
              <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full">{status}</div>
              
              {/* Logo watermark */}
              <img src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png" alt="Blue vs Blue" className="absolute bottom-3 right-3 h-12 w-auto opacity-80 rounded z-20" />
              
              {/* Commercial overlay */}
              {showCommercial && <div className="absolute inset-0 bg-gradient-to-br from-deep-blue to-[#1a3a5c] flex flex-col items-center justify-center z-10"><img src="https://customer-assets.emergentagent.com/job_6cd70ab0-164b-4026-90ab-4bbce9b2006d/artifacts/koo5s91n_BluevsBlue1%20%281%29.png" alt="" className="h-20 rounded-lg mb-3" /><p className="text-white font-bold text-xl">We'll Be Right Back</p></div>}
              
              {/* Text overlay */}
              {showOverlay && overlayText && <div className="absolute bottom-16 left-0 right-0 z-20"><div className="mx-4 bg-deep-blue/90 backdrop-blur-sm border-l-4 border-awareness-blue px-4 py-2"><p className="text-white font-bold text-sm">{overlayText}</p></div></div>}
              
              {/* News ticker */}
              {scrollActive && scrollText && <div className="absolute bottom-0 left-0 right-0 bg-red-600 py-1.5 overflow-hidden z-20"><div className="flex animate-scroll"><span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span><span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span><span className="text-white font-bold text-sm whitespace-nowrap px-8">{scrollText}</span></div></div>}
              
              {!isLive && <div className="absolute inset-0 flex items-center justify-center bg-black/80"><div className="text-center"><Video className="w-16 h-16 text-slate-500 mx-auto mb-3" /><p className="text-white text-lg font-semibold">Stream ended</p></div></div>}
            </div>
            <div className="mt-4"><h2 className="text-white text-lg font-bold">{stream.title}</h2><p className="text-slate-400 text-sm">{stream.broadcaster_name}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {isAdmin && <button onClick={() => setDirectorOpen(!directorOpen)} className="bg-awareness-blue text-white text-xs px-3 py-1.5 rounded hover:bg-awareness-blue/80">{directorOpen ? "Hide Director" : "Director Mode"}</button>}
                {isAdmin && <Button size="sm" onClick={() => setEndDialogOpen(true)} className="bg-red-500 text-white hover:bg-red-600"><StopCircle className="w-3 h-3 mr-1" /> Stop Stream</Button>}
                <Button size="sm" variant="outline" className="text-white border-slate-600 text-xs" onClick={() => { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank", "width=600,height=400"); }}>Facebook</Button>
                <Button size="sm" variant="outline" className="text-white border-slate-600 text-xs" onClick={() => { window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent("Watching live on Blue vs Blue!")}&url=${encodeURIComponent(window.location.href)}`, "_blank", "width=600,height=400"); }}>X / Twitter</Button>
                <Button size="sm" variant="outline" className="text-white border-slate-600 text-xs" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied!"); }}>Copy Link</Button>
              </div>
            </div>
          </div>

          {/* Chat */}
          <div className="bg-slate-800 rounded-xl flex flex-col h-[calc(100vh-120px)] max-h-[600px]">
            <div className="p-3 border-b border-slate-700"><h3 className="text-white font-semibold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Live Chat</h3></div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">{chatMessages.map((m, i) => <div key={i} className="text-sm"><span className="font-semibold text-awareness-blue">{m.user_name}: </span><span className="text-slate-300">{m.content}</span></div>)}<div ref={chatEndRef} /></div>
            {user && <div className="p-3 border-t border-slate-700 flex gap-2"><Input value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Message..." className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400" /><Button size="sm" onClick={sendChat} className="bg-awareness-blue text-white"><Send className="w-4 h-4" /></Button></div>}
          </div>
        </div>
      </div>
      {/* End Stream Dialog */}
      {isAdmin && (
        <Dialog open={endDialogOpen} onOpenChange={setEndDialogOpen}>
          <DialogContent className="sm:max-w-sm" data-testid="end-stream-dialog">
            <DialogHeader><DialogTitle>End Stream</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500 mb-4">What would you like to do?</p>
            <div className="space-y-2">
              <Button onClick={async () => { try { await axios.post(`${API}/streams/${stream.stream_id}/end`, {}, { withCredentials: true }); toast.success("Stream saved"); } catch {} setEndDialogOpen(false); setIsLive(false); }} className="w-full bg-deep-blue text-white justify-start gap-2"><Save className="w-4 h-4" /> Save & End</Button>
              <Button onClick={async () => { try { await axios.post(`${API}/streams/${stream.stream_id}/end`, {}, { withCredentials: true }); } catch {} navigator.clipboard?.writeText(window.location.href); toast.success("Link copied & saved"); setEndDialogOpen(false); setIsLive(false); }} variant="outline" className="w-full justify-start gap-2"><Share2 className="w-4 h-4" /> Share & End</Button>
              <Button onClick={async () => { try { await axios.post(`${API}/streams/${stream.stream_id}/end`, {}, { withCredentials: true }); await axios.put(`${API}/streams/${stream.stream_id}/visibility`, {}, { withCredentials: true }); toast.success("Archived"); } catch {} setEndDialogOpen(false); setIsLive(false); }} variant="outline" className="w-full justify-start gap-2"><Eye className="w-4 h-4" /> Archive</Button>
              <Button onClick={async () => { try { await axios.delete(`${API}/streams/${stream.stream_id}`, { withCredentials: true }); toast.success("Deleted"); } catch {} setEndDialogOpen(false); setIsLive(false); }} variant="outline" className="w-full justify-start gap-2 text-red-500"><Trash2 className="w-4 h-4" /> Delete</Button>
              <Button onClick={() => setEndDialogOpen(false)} variant="ghost" className="w-full justify-start gap-2 text-slate-500"><X className="w-4 h-4" /> Cancel</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

export default function LiveStreamPage() {
  const { streamId } = useParams();
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [pastStreams, setPastStreams] = useState([]);
  const [activeStream, setActiveStream] = useState(null);
  const [isBroadcaster, setIsBroadcaster] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [canGoLive, setCanGoLive] = useState(false);
  const cameraStreamRef = useRef(null);

  useEffect(() => {
    if (streamId) fetchStream();
    else fetchStreams();
    if (user) checkLivePermission();
  }, [streamId, user]);

  const checkLivePermission = async () => {
    try {
      const res = await axios.get(`${API}/user/can-go-live`, { withCredentials: true });
      setCanGoLive(res.data.can_go_live);
    } catch {}
  };

  const fetchStreams = async () => {
    try {
      const [a, b] = await Promise.all([axios.get(`${API}/streams?status=live`), axios.get(`${API}/streams?status=ended`)]);
      setStreams(a.data || []); setPastStreams(b.data || []);
    } catch {}
  };

  const fetchStream = async () => {
    try {
      const res = await axios.get(`${API}/streams/${streamId}`);
      if (res.data.status === "live") {
        setActiveStream(res.data);
        // Only set as broadcaster if we have a local camera stream (came from Go Live flow)
        // This prevents the same user on another device being treated as broadcaster
        setIsBroadcaster(false);
      }
    } catch { toast.error("Stream not found"); }
  };

  const goLive = async () => {
    if (!title.trim() || !cameraStreamRef.current) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/streams`, { title }, { withCredentials: true });
      setActiveStream(res.data.stream); setIsBroadcaster(true);
      window.history.pushState(null, "", `/live/${res.data.stream.stream_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setCreating(false); }
  };

  const handleEnd = () => {
    if (cameraStreamRef.current) { cameraStreamRef.current.getTracks().forEach(t => t.stop()); cameraStreamRef.current = null; }
    setActiveStream(null); setIsBroadcaster(false); setLocalStream(null); setShowPreview(false);
    window.history.pushState(null, "", "/live");
    fetchStreams();
  };

  if (activeStream) {
    return <Layout>{isBroadcaster ? <BroadcasterView stream={activeStream} user={user} localStream={localStream} onEnd={handleEnd} /> : <ViewerView stream={activeStream} user={user} />}</Layout>;
  }

  return (
    <Layout>
      <section className="gradient-hero py-12"><div className="container-custom"><span className="text-awareness-blue font-semibold text-sm uppercase tracking-wide">Live</span><h1 className="text-heading-1 text-white mt-3 mb-3">Live Streams</h1><p className="text-white/80 text-lg">Watch live events or start your own stream.</p></div></section>
      <section className="section-spacing">
        <div className="container-custom">
          {!user && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8 max-w-xl text-center" data-testid="live-login-prompt">
              <Video className="w-12 h-12 text-deep-blue mx-auto mb-3" />
              <h3 className="font-bold text-deep-blue text-lg mb-2">Join the Live Experience</h3>
              <p className="text-slate-500 text-sm mb-5">Sign in to watch live streams, chat with the community, and go live yourself.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/login"><Button className="bg-deep-blue text-white">Log In</Button></Link>
                <Link to="/register"><Button variant="outline">Sign Up</Button></Link>
              </div>
            </div>
          )}
          {user && canGoLive && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8 max-w-xl" data-testid="start-stream-box">
              <h3 className="font-bold text-deep-blue mb-3">Go Live</h3>
              {!showPreview ? (
                <div><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Stream title..." className="mb-3" data-testid="stream-title-input" /><Button onClick={() => { if (!title.trim()) { toast.error("Enter a title"); return; } setShowPreview(true); }} className="bg-deep-blue text-white"><Camera className="w-4 h-4 mr-2" /> Preview Camera</Button></div>
              ) : (
                <div><p className="text-sm text-slate-600 mb-3">Stream: <strong>{title}</strong></p><CameraPreview onStreamReady={(s) => { cameraStreamRef.current = s; setLocalStream(s); }} /><div className="flex gap-3 mt-4"><Button onClick={goLive} disabled={creating || !localStream} className="bg-red-500 text-white hover:bg-red-600" data-testid="go-live-btn"><Radio className="w-4 h-4 mr-2 animate-pulse" /> {creating ? "Starting..." : "Go Live Now"}</Button><Button variant="outline" onClick={() => { setShowPreview(false); if (cameraStreamRef.current) { cameraStreamRef.current.getTracks().forEach(t => t.stop()); setLocalStream(null); } }}>Cancel</Button></div></div>
              )}
            </div>
          )}
          {streams.length > 0 && <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">{streams.map(s => <Link key={s.stream_id} to={`/live/${s.stream_id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"><div className="aspect-video bg-slate-900 flex items-center justify-center relative"><Video className="w-12 h-12 text-slate-600" /><div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1"><Radio className="w-3 h-3 animate-pulse" /> LIVE</div></div><div className="p-4"><h3 className="font-semibold text-deep-blue">{s.title}</h3><p className="text-sm text-slate-500 mt-1">{s.broadcaster_name}</p></div></Link>)}</div>}
          {pastStreams.length > 0 && <div><h2 className="text-lg font-bold text-deep-blue mb-4">Past Streams</h2><div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{pastStreams.map(s => <div key={s.stream_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden"><div className="aspect-video bg-slate-900 flex items-center justify-center relative"><Video className="w-12 h-12 text-slate-600" />{s.has_recording && <div className="absolute bottom-3 right-3 bg-awareness-blue text-white text-xs px-2 py-1 rounded flex items-center gap-1"><Play className="w-3 h-3" /> Recording</div>}</div><div className="p-4"><h3 className="font-semibold text-deep-blue">{s.title}</h3><p className="text-sm text-slate-500 mt-1">{s.broadcaster_name}</p>{s.has_recording && <a href={`${API}/streams/${s.stream_id}/recording`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-sm text-awareness-blue hover:underline"><Play className="w-4 h-4" /> Watch</a>}</div></div>)}</div></div>}
          {streams.length === 0 && pastStreams.length === 0 && <div className="text-center py-16 bg-slate-50 rounded-xl"><Video className="w-12 h-12 text-slate-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-slate-600">No streams yet</h3></div>}
        </div>
      </section>
    </Layout>
  );
}
